import React, { useEffect, useState } from "react";
import { Modal } from "newui/components/ui/Modal";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { useRenameRole } from "newui/features/RolesPermissions/hooks/useRoles";
import type { RoleResource } from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { showToast } from "utils/helpers";

export interface RenameRoleModalProps {
	isOpen: boolean;
	role: RoleResource | null;
	onClose: () => void;
}

const BASE_ROLE_ERROR_MESSAGE = "System roles can't be renamed.";

export const RenameRoleModal = ({ isOpen, role, onClose }: RenameRoleModalProps) => {
	const [name, setName] = useState("");
	const [inlineError, setInlineError] = useState<string | undefined>(undefined);
	const renameRoleMutation = useRenameRole();

	useEffect(() => {
		if (role) {
			setName(role.name);
			setInlineError(undefined);
		}
	}, [role]);

	const handleClose = () => {
		renameRoleMutation.reset();
		setInlineError(undefined);
		onClose();
	};

	const handleConfirm = () => {
		if (!role || !name.trim()) {
			return;
		}

		setInlineError(undefined);

		renameRoleMutation.mutate(
			{ roleId: role.id, payload: { name: name.trim() } },
			{
				onSuccess: () => {
					showToast(`Role renamed to "${name.trim()}"`, "Role updated", true, "rename-role-success");
					handleClose();
				},
				onError: (error: unknown) => {
					const status = (error as { response?: { status?: number } })?.response?.status;

					if (status === 403) {
						setInlineError(BASE_ROLE_ERROR_MESSAGE);
						showToast(BASE_ROLE_ERROR_MESSAGE, "Rename failed", false, "rename-role-403");
						return;
					}

					showToast("Unable to rename this role. Please try again.", "Rename failed", false, "rename-role-error");
				},
			}
		);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title="Rename Role"
			iconType="edit"
			size="md"
			primaryButton={{
				text: renameRoleMutation.isPending ? "Saving..." : "Save",
				btntype: "primary",
				disabled: renameRoleMutation.isPending || !name.trim(),
				isLoading: renameRoleMutation.isPending,
				onClick: handleConfirm,
			}}
			secondaryButton={{
				text: "Cancel",
				btntype: "secondary",
				disabled: renameRoleMutation.isPending,
				onClick: handleClose,
			}}
		>
			<div data-testid="rename-role-modal-content">
				<InputWithLabel
					label="Role name"
					value={name}
					onChange={(e) => setName(e.target.value)}
					testId="rename-role-name-input"
					errorText={inlineError}
					required
				/>
			</div>
		</Modal>
	);
};
